import React, { useEffect, useState, useCallback } from "react";
import { Bell, Settings2, Megaphone, Check, CheckCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ADMIN_ROLES = ["admin", "super_admin", "franchise_owner", "branch_manager"];

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "az önce";
  if (s < 3600) return `${Math.floor(s / 60)} dk önce`;
  if (s < 86400) return `${Math.floor(s / 3600)} sa önce`;
  return `${Math.floor(s / 86400)} gün önce`;
}

export default function NotificationBell({ role }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [prefs, setPrefs] = useState({ in_app: true, email: true, whatsapp: true });
  const [bcOpen, setBcOpen] = useState(false);
  const isAdmin = ADMIN_ROLES.includes(role);

  const loadCount = useCallback(() => {
    api.get("/notifications/unread-count").then((r) => setUnread(r.data.unread)).catch(() => {});
  }, []);
  const loadList = useCallback(() => {
    api.get("/notifications").then((r) => { setItems(r.data.items); setUnread(r.data.unread); }).catch(() => {});
  }, []);

  useEffect(() => { loadCount(); const t = setInterval(loadCount, 30000); return () => clearInterval(t); }, [loadCount]);
  useEffect(() => { if (open) loadList(); }, [open, loadList]);

  const markRead = async (id) => {
    await api.post(`/notifications/${id}/read`).catch(() => {});
    setItems((p) => p.map((n) => (n.id === id ? { ...n, read: true } : n)));
    setUnread((u) => Math.max(0, u - 1));
  };
  const markAll = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    setItems((p) => p.map((n) => ({ ...n, read: true })));
    setUnread(0);
  };

  const openPrefs = async () => {
    setOpen(false);
    try { const r = await api.get("/notifications/preferences"); setPrefs(r.data); } catch { /* ignore */ }
    setPrefsOpen(true);
  };
  const savePrefs = async (next) => {
    setPrefs(next);
    try { await api.put("/notifications/preferences", next); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button data-testid="notification-bell-button" className="relative p-2 rounded-lg text-[#A0A0B2] hover:text-white hover:bg-[#1B1B27] transition-colors">
            <Bell size={19} />
            {unread > 0 && (
              <span data-testid="notification-unread-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF5500] text-white text-[10px] font-bold flex items-center justify-center">
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 p-0 bg-[#13131C] border-[#222230] text-white" data-testid="notification-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#222230]">
            <p className="font-semibold text-sm">Bildirimler</p>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button onClick={markAll} data-testid="notification-mark-all-button" className="text-[11px] text-[#FF5500] hover:underline flex items-center gap-1">
                  <CheckCheck size={13} /> Tümü okundu
                </button>
              )}
              {isAdmin && (
                <button onClick={() => { setOpen(false); setBcOpen(true); }} data-testid="notification-broadcast-open-button" title="Duyuru gönder" className="p-1.5 rounded-md text-[#A0A0B2] hover:text-white hover:bg-[#1B1B27]">
                  <Megaphone size={15} />
                </button>
              )}
              <button onClick={openPrefs} data-testid="notification-prefs-open-button" title="Bildirim ayarları" className="p-1.5 rounded-md text-[#A0A0B2] hover:text-white hover:bg-[#1B1B27]">
                <Settings2 size={15} />
              </button>
            </div>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-10 text-center text-[#68687B] text-sm" data-testid="notification-empty">Henüz bildirim yok</div>
            ) : (
              items.map((n) => (
                <div key={n.id} data-testid={`notification-item-${n.id}`} onClick={() => !n.read && markRead(n.id)}
                  className={`px-4 py-3 border-b border-[#1A1A24] cursor-pointer hover:bg-[#181824] transition-colors ${n.read ? "opacity-60" : ""}`}>
                  <div className="flex items-start gap-2">
                    {!n.read && <span className="mt-1.5 w-2 h-2 rounded-full bg-[#FF5500] flex-shrink-0" />}
                    <div className={n.read ? "pl-4" : ""}>
                      <p className="text-sm font-medium text-white leading-tight">{n.title}</p>
                      <p className="text-xs text-[#A0A0B2] mt-0.5 leading-snug">{n.body}</p>
                      <p className="text-[10px] text-[#68687B] mt-1">{timeAgo(n.created_at)}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </PopoverContent>
      </Popover>

      {/* Preferences */}
      <Dialog open={prefsOpen} onOpenChange={setPrefsOpen}>
        <DialogContent className="bg-[#13131C] border-[#222230] text-white max-w-sm" data-testid="notification-prefs-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Bildirim Ayarları</DialogTitle>
            <DialogDescription className="text-[#68687B]">Hangi kanallardan bildirim almak istediğini seç.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {[
              { key: "in_app", label: "Uygulama içi", desc: "Zil bildirimleri" },
              { key: "email", label: "E-posta", desc: "Önemli güncellemeler" },
              { key: "whatsapp", label: "WhatsApp", desc: "Anlık hatırlatmalar" },
            ].map((c) => (
              <div key={c.key} className="flex items-center justify-between">
                <div><p className="text-sm font-medium text-white">{c.label}</p><p className="text-xs text-[#68687B]">{c.desc}</p></div>
                <Switch data-testid={`pref-toggle-${c.key}`} checked={!!prefs[c.key]} onCheckedChange={(v) => savePrefs({ ...prefs, [c.key]: v })} />
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {isAdmin && <BroadcastDialog open={bcOpen} onOpenChange={setBcOpen} role={role} />}
    </>
  );
}

function BroadcastDialog({ open, onOpenChange, role }) {
  const [form, setForm] = useState({ audience: "members", title: "", message: "", send_email: false, send_whatsapp: false });
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (!form.title.trim() || !form.message.trim()) { toast.error("Başlık ve mesaj gerekli."); return; }
    setSending(true);
    try {
      const r = await api.post("/notifications/broadcast", form);
      toast.success(`Duyuru gönderildi (${r.data.sent} kişi).`);
      onOpenChange(false);
      setForm({ audience: "members", title: "", message: "", send_email: false, send_whatsapp: false });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#13131C] border-[#222230] text-white max-w-md" data-testid="broadcast-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2"><Megaphone size={18} className="text-[#FF5500]" /> Duyuru Gönder</DialogTitle>
          <DialogDescription className="text-[#68687B]">
            {role === "branch_manager" ? "Kendi şubendeki kişilere" : "Seçtiğin kitleye"} uygulama-içi duyuru gönder.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div>
            <Label className="text-xs text-[#A0A0B2]">Kitle</Label>
            <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })}>
              <SelectTrigger data-testid="broadcast-audience-select" className="bg-[#0E0E14] border-[#2D2D3F] text-white mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#13131C] border-[#2D2D3F] text-white">
                <SelectItem value="members">Üyeler</SelectItem>
                <SelectItem value="trainers">Antrenörler</SelectItem>
                <SelectItem value="all">Herkes</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-[#A0A0B2]">Başlık</Label>
            <Input data-testid="broadcast-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-[#0E0E14] border-[#2D2D3F] text-white mt-1" placeholder="Örn: Yeni ders saatleri" />
          </div>
          <div>
            <Label className="text-xs text-[#A0A0B2]">Mesaj</Label>
            <Textarea data-testid="broadcast-message-input" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="bg-[#0E0E14] border-[#2D2D3F] text-white mt-1" rows={3} placeholder="Duyuru metni..." />
          </div>
          <div className="flex items-center gap-4 pt-1">
            <label className="flex items-center gap-2 text-sm text-[#A0A0B2]"><Switch data-testid="broadcast-email-toggle" checked={form.send_email} onCheckedChange={(v) => setForm({ ...form, send_email: v })} /> E-posta da gönder</label>
            <label className="flex items-center gap-2 text-sm text-[#A0A0B2]"><Switch data-testid="broadcast-whatsapp-toggle" checked={form.send_whatsapp} onCheckedChange={(v) => setForm({ ...form, send_whatsapp: v })} /> WhatsApp</label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-[#A0A0B2]">Vazgeç</Button>
          <Button onClick={submit} disabled={sending} data-testid="broadcast-send-button" className="bg-[#FF5500] hover:bg-[#E64D00] text-white font-bold">{sending ? "Gönderiliyor..." : "Gönder"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
